import { UsersAndUserGroupsDataModel } from "../UserGroupsManagement/userGroupsManagement";
import { HubItemCategoryModel } from "../Hub/hub";
import { environmentHttpClient } from "../httpClient";
import {
	HubItemAccessModel
} from "../Announcements/announcements";
import { TagSaveModel, TagViewModel } from "client/Api/Tags/tags";
import {
	HubItemLayoutSaveModel,
	HubItemMediaSaveModel,
	HubItemMediaComposerViewModel,
	HubItemLayoutComposerViewModel,
	HubLinkedDocumentSaveModel,
	HubHierarchyListNodeModel
} from "../Hub/common.composer";
import { HubItemImageRestrictionsModel, HubLinkedDocumentViewModel } from "../Hub/common";
import { AttendeeExportModel, EventAttendeeKind } from "../Hub/Events/eventViewer";
import { UserIdentityModel } from "../UserProfile/service";
import { DocumentLocationViewModel } from "../Hub/hubSettings";

export enum EventStatus {
	Draft = 1,
	Published = 2,
	Planned = 3,
	Archived = 4
}

export enum LocationType {
	Online = 1,
	Physical = 2,
	Hybrid = 3
}

export enum TicketType {
	Free = 1,
	Paid = 2
}

export type EventSaveModel = {
	id: number | null;
	title: string;
	layout: HubItemLayoutSaveModel;
	media: HubItemMediaSaveModel;
	isForEveryoneInTheCategory: boolean;
	access: HubItemAccessModel | null;
	removedAttendeeIds: readonly number[];
	categoryId: number | null;
	startTime: Date | null;
	endTime: Date | null;
	publicationDate: Date | null;
	locationType: LocationType | null;
	location: string | null;
	onlineEventUrl: string | null;
	ticketType: TicketType | null;
	signUpDeadline: Date | null;
	availableSpots: number | null;
	externalUrl: string | null;
	tags: readonly TagSaveModel[];
	showAutomatically: boolean;
	linkedDocuments: readonly HubLinkedDocumentSaveModel[];
	isAttendanceEnabled: boolean;
	shouldShowAuthor: boolean;
	areCommentsEnabled: boolean;
};

export type AttendeeIdWithKind = {
	id: number;
	kind: EventAttendeeKind;
};

export type EditEventPageViewModel = {
	eventModel: EventViewModel;
	hasPermissionToCreateNewCategory: boolean;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	eventHierarchy: readonly HubHierarchyListNodeModel[];
	imageRestrictions: HubItemImageRestrictionsModel;
	defaultImageLocation: DocumentLocationViewModel | null;
	attendeeIdsWithKind: readonly AttendeeIdWithKind[];
	createdBy: UserIdentityModel | null;
	canSendDirectPopups: boolean;
	isPixabayImageGalleryAvailable: boolean;
};

export type EventViewModel = {
	id: number | null;
	title: string;
	layout: HubItemLayoutComposerViewModel;
	media: HubItemMediaComposerViewModel;
	isForEveryoneInTheCategory: boolean;
	access: HubItemAccessModel | null;
	status: EventStatus;
	categoryId: number | null;
	startTime: Date | null;
	endTime: Date | null;
	publicationDate: Date | null;
	locationType: LocationType | null;
	location: string | null;
	onlineEventUrl: string | null;
	ticketType: TicketType | null;
	signUpDeadline: Date | null;
	availableSpots: number | null;
	externalUrl: string | null;
	tags: readonly TagViewModel[];
	showAutomatically: boolean;
	linkedDocuments: readonly HubLinkedDocumentViewModel[] | null;
	isAttendanceEnabled: boolean;
	shouldShowAuthor: boolean;
	areCommentsEnabled: boolean;
};

export type EventUpdateSaveModel = {
	eventSaveModel : EventSaveModel;
	shouldPublishEvent: boolean;
	shouldSendNotifications: boolean | null;
};

export type EventCreateSaveModel = {
	eventSaveModel : EventSaveModel;
	shouldPublishEvent: boolean;
};

export type EventSaveRequestModel = EventUpdateSaveModel | EventCreateSaveModel;

export type EventSaveResult = {
	eventId: number;
};

export type EventListItemModel = {
	id: number;
	title: string;
	publicationDate: Date | string | null;
	startDate: Date | string | null;
	endDate: Date | string | null;
	createdByUserName: string;
	modifiedByUserName: string;
	modifiedDate: Date | string;
	availableSpots: number | null;
	totalAttendees: number | null;
	locationType: LocationType;
	tags: readonly TagViewModel[];
	status: EventStatus;
	categoryName: string;
	categoryId: number | null;
	reactionsCount: number;
	commentsCount: number;
};

export type EventListModel = {
	events: readonly EventListItemModel[];
	categories: readonly HubItemCategoryModel[];
	canCreateTemplates: boolean;
};

export function getNewEventModel(
	eventToCopyId: undefined,
	templateId: undefined,
	signal?: AbortSignal
): Promise<EditEventPageViewModel>;
export function getNewEventModel(
	eventToCopyId: number,
	templateId: undefined,
	signal?: AbortSignal
): Promise<EditEventPageViewModel>;
export function getNewEventModel(
	eventToCopyId: undefined,
	templateId: number,
	signal?: AbortSignal
): Promise<EditEventPageViewModel>;
export function getNewEventModel(
	eventToCopyId: number | undefined,
	templateId: number | undefined,
	signal?: AbortSignal
): Promise<EditEventPageViewModel> {
	return environmentHttpClient.get<EditEventPageViewModel>(
		"UiApi/EventsManagement/Add",
		{ eventToCopyId, templateId },
		{ signal }
	);
}

export function createEvent(
	model: EventCreateSaveModel
): Promise<EventSaveResult> {
	return environmentHttpClient.post<EventSaveResult>(
		"UiApi/EventsManagement",
		model
	);
}

export function getEditEventModel(id: number, signal?: AbortSignal): Promise<EditEventPageViewModel> {
	return environmentHttpClient.get<EditEventPageViewModel>(
		`UiApi/EventsManagement/Edit/${id}`,
		undefined,
		{ signal }
	);
}

export function updateEvent(
	id: number,
	model: EventUpdateSaveModel
): Promise<EventSaveResult> {
	return environmentHttpClient.put<EventSaveResult>(
		`UiApi/EventsManagement/${id}`,
		model
	);
}

export function deleteEvents(ids: number[], signal?: AbortSignal): Promise<void> {
	return environmentHttpClient.delete<void>(
		"UiApi/EventsManagement",
		ids,
		{ signal }
	);
}

export function archiveEvents(
	ids: number[],
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		"UiApi/EventsManagement/Archive",
		ids,
		{ signal }
	);
}

export function listEvents(
	signal?: AbortSignal
): Promise<EventListModel> {
	return environmentHttpClient.get<EventListModel>(
		"UiApi/EventsManagement/List",
		undefined,
		{ signal }
	);
}

export function listAttendeesForExport(
	id: number,
	signal?: AbortSignal
): Promise<readonly AttendeeExportModel[]> {
	return environmentHttpClient.get<readonly AttendeeExportModel[]>(
		`UiApi/EventsManagement/${id}/Attendees/Export`,
		undefined,
		{ signal }
	);
}
